import { CoreImageView } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { AssetsSvgMap, THandleChangeInputPhone, TMargin, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import PhoneInput, { PhoneInputProps } from 'react-phone-input-2';
import { TConsumerInputPhoneData, TConsumerItem, TConsumerListData } from '../../types/data';
import { RowEnum } from '../../types/enums';
import { TInputOption, TListOption } from '../../types/options';
import { ButtonRowView } from '../Buttons/ButtonRowView';
import { getHref } from '../Image/imageUtils';
import { ListSelectView } from '../List/select/ListSelectView';
import { FlagIcon } from '../UI/FlagIcon/FlagIcon';
import { Modal } from '../UI/Modal/Modal';
import { PopupComponent } from '../UI/Popup/PopupComponent';
import { TextCommon } from '../UI/Text/TextCommon';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import styles from './inputPhone.module.css';
import { convertItemsToListSelect } from './utils/inputPhone';
import { PHONE_CUSTOM_MASK } from './utils/masks';

export type TInputPhoneViewProps = {
    /**
     * Optional. Forwarded to InputWrapper only for theme resolution; when omitted a
     * minimal widget is synthesized from `uid`. No data or text is read from it.
     */
    widget?: TWidget<TConsumerInputPhoneData, TInputOption>;
    /** Value for the `data-test` attribute (widget uid in server-driven scenarios). */
    uid?: string;
    label?: string;
    helperText?: string;
    isError?: boolean;
    errorMessage?: string;
    isFocused?: boolean;
    isDisabled?: boolean;
    isHidden?: boolean;
    margin?: TMargin;
    /** ISO2 country code driving the flag icon and the phone field country. */
    iso2?: string;
    /** Whether the country picker has selectable items (enables the dropdown trigger). */
    hasItems?: boolean;
    /** Selectable country items shown in the picker modal. */
    items?: TConsumerItem[];
    /** Pre-resolved phone value shown in the field (falls back to the country prefix). */
    displayValue?: string;
    /** Raw redux value, used as the phone fallback in the error popup description. */
    value?: string;
    /** Currently selected country item id (widget state, owned by InputPhoneWidget). */
    selectedItemId?: string;
    /** Header text shown above the country picker list. */
    selectPrefixHeaderText?: string;
    /** Error popup description template (supports `$phone` and `$countryName` placeholders). */
    errorDescriptionTemplate?: string;
    /** Label/icon for the error popup "retry" button (reopens the country picker). */
    retryButtonLabel?: string;
    retryButtonIcon?: string;
    /** Label/icon for the error popup "edit" button (refocuses the field). */
    editButtonLabel?: string;
    editButtonIcon?: string;
    /** Ref forwarded to react-phone-input-2 so the parent can focus/read the field. */
    phoneRef?: React.RefObject<HTMLInputElement | null>;
    autoFocus?: boolean;
    name?: string;
    autoComplete?: string;
    required?: boolean;
    maxLength?: number;
    tabIndex?: number;
    /** Opens/closes the app-level modal (from useApp, provided by the Widget). */
    setModal: Dispatch<SetStateAction<ReactNode>>;
    /** Opens/closes the app-level error popup (from useApp, provided by the Widget). */
    setPopup: Dispatch<SetStateAction<ReactNode>>;
    /** Behavior callback invoked when a country is picked in the modal list (owned by the Widget). */
    onSelectItem: (uid: string, item: TConsumerItem) => void;
    onFocus?: PhoneInputProps['onFocus'];
    onBlur?: PhoneInputProps['onBlur'];
    onChange?: THandleChangeInputPhone;
};

/**
 * Presentational phone input. Owns rendering only — the input wrapper, the
 * country-flag dropdown trigger, the react-phone-input-2 field, and the two
 * overlays (the country-picker modal and the error popup) together with the
 * mechanism that opens them.
 *
 * Behavior state and hooks live in InputPhoneWidget; this component receives
 * pre-resolved values plus the `setModal`/`setPopup`/`onSelectItem` callbacks
 * it needs to open the overlays. All list items and texts arrive as explicit
 * props — nothing is read from a `widget` object.
 */
const InputPhoneView = ({
    widget,
    uid,
    label,
    helperText,
    isError = false,
    errorMessage,
    isFocused = false,
    isDisabled = false,
    isHidden = false,
    margin,
    iso2,
    hasItems = false,
    items,
    displayValue,
    value,
    selectedItemId,
    selectPrefixHeaderText,
    errorDescriptionTemplate,
    retryButtonLabel,
    retryButtonIcon,
    editButtonLabel,
    editButtonIcon,
    phoneRef,
    autoFocus = false,
    name,
    autoComplete,
    required,
    maxLength,
    tabIndex,
    setModal,
    setPopup,
    onSelectItem,
    onFocus,
    onBlur,
    onChange,
}: TInputPhoneViewProps) => {
    // The View never reads data/text off a widget. It synthesizes the minimal shape
    // InputWrapper needs for theme resolution from `uid` when no widget is provided.
    const themeWidget = widget ?? ({ uid: uid ?? '', data: {}, options: {} } as TWidget<TConsumerInputPhoneData, TInputOption>);

    const listData = useMemo(
        () =>
            ({
                ...widget?.data,
                value: selectedItemId,
                items: convertItemsToListSelect(items, iso2 as string),
            }) as unknown as TConsumerListData & TConsumerInputPhoneData,
        [widget?.data, items, selectedItemId, iso2],
    );

    const widgetForListSelect = useMemo(
        () => ({ uid: `${uid}-modal`, options: {}, data: listData }) as unknown as TWidget<TConsumerInputPhoneData, TListOption>,
        [uid, listData],
    );

    const modalContent = (
        <Modal isCanCloseModal={true} iconName={AssetsSvgMap.ic_arrow_back} onHandleClick={() => setModal(null)}>
            <ConditionalRender condition={!!selectPrefixHeaderText}>
                <TextCommon
                    isWidget={false}
                    options={{ variant: 'header', margin: { top: 16, left: 24, right: 24, bottom: 32 } }}
                    content={selectPrefixHeaderText}
                />
            </ConditionalRender>
            <ListSelectView
                rowId={RowEnum.SELECTOR_EXTRA}
                isOpenInModal={true}
                isHidden={false}
                isDisabled={false}
                widget={widgetForListSelect as unknown as TWidget<TConsumerListData & TConsumerInputPhoneData, TListOption>}
                dataForListSelect={listData}
                onSelectItem={onSelectItem}
            />
        </Modal>
    );

    const onOpenCountryList = () => {
        if (hasItems && !isDisabled) {
            setModal(modalContent);
        }
    };

    const errorPopup = () => {
        const selectedItem = items?.find((i) => i.id === selectedItemId) as TConsumerItem;
        const errorDescription = errorDescriptionTemplate
            //@ts-expect-error
            ?.replace('$phone', phoneRef?.current?.state?.formattedNumber ?? value)
            ?.replace('$countryName', selectedItem?.countryName ?? '');
        return (
            <PopupComponent
                header={<TextCommon isWidget={false} content={errorDescription} />}
                internalContent={[
                    <ButtonRowView
                        key="button_ic_check"
                        label={retryButtonLabel}
                        iconName={retryButtonIcon ?? 'ic_arrow_retry'}
                        onClick={() => {
                            setPopup(null);
                            onOpenCountryList();
                        }}
                    />,
                    <ButtonRowView
                        key="button_ic_check"
                        label={editButtonLabel}
                        iconName={editButtonIcon ?? 'ic_pencil'}
                        onClick={() => {
                            setPopup(null);
                            //@ts-expect-error
                            // https://github.com/bl00mber/react-phone-input-2/issues/187#issuecomment-799988994
                            phoneRef?.current?.numberInputRef?.focus?.();
                        }}
                    />,
                ]}
            />
        );
    };

    const isShowErrorHintButton = isError && !!errorDescriptionTemplate;

    return (
        <InputWrapper
            dataTest={uid}
            widget={themeWidget}
            labelText={label}
            helperText={helperText}
            isError={isError}
            isShowErrorHintButton={isShowErrorHintButton}
            handleErrorClick={() => setPopup(errorPopup)}
            isDisabled={isDisabled}
            isHidden={isHidden}
            errorMessage={errorMessage}
            isFocused={isFocused}
            isFilled
            marginTop={margin?.top}
            marginRight={margin?.right}
            marginBottom={margin?.bottom}
            marginLeft={margin?.left}
            leftInputComponent={
                <div
                    data-test={`${uid}-dropdown`}
                    className={clsx(styles.flagIconDropdown, isDisabled && styles.isDisabled)}
                    onClick={() => onOpenCountryList()}
                >
                    <FlagIcon name={iso2} />
                    <ConditionalRender condition={hasItems}>
                        <div className={styles.iconContainer}>
                            <CoreImageView
                                src={getHref(AssetsSvgMap.ic_chevron_down)}
                                styleProps={{
                                    iconColor: `var(--color-black)`,
                                }}
                            />
                        </div>
                    </ConditionalRender>
                </div>
            }
        >
            <PhoneInput
                //@ts-expect-error
                ref={phoneRef}
                inputProps={{
                    autoFocus,
                    name,
                    autoComplete,
                    inputMode: 'tel',
                    required,
                    maxLength,
                    readOnly: isDisabled,
                    tabIndex,
                }}
                country={iso2}
                value={displayValue}
                specialLabel={''}
                placeholder={''}
                disableDropdown={true}
                countryCodeEditable={false}
                defaultMask={'... ... ... ... ..'}
                masks={PHONE_CUSTOM_MASK}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onChange}
            />
        </InputWrapper>
    );
};

export { InputPhoneView };
